import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface Currency {
  code: string;
  symbol: string;
  name: string;
}

interface StoreContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => void;
}

const defaultCurrency: Currency = {
  code: 'RSD',
  symbol: 'RSD',
  name: 'Serbian Dinar'
};

const StoreContext = createContext<StoreContextType>({
  currency: defaultCurrency,
  setCurrency: (currency: Currency) => {
    console.warn('setCurrency was called without StoreProvider');
  }
});

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);

  useEffect(() => {
    const loadStoreSettings = async () => {
      try {
        // Prvo dohvatamo store settings da dobijemo ID default valute
        const { data: settings, error: settingsError } = await supabase
          .from('store_settings')
          .select('default_currency_id')
          .single();

        if (settingsError) throw settingsError;

        if (settings?.default_currency_id) {
          // Zatim dohvatamo detalje o valuti
          const { data: currencyData, error: currencyError } = await supabase
            .from('currencies')
            .select('code, symbol, name')
            .eq('id', settings.default_currency_id)
            .single();

          if (currencyError) throw currencyError;

          if (currencyData) {
            console.log('Setting currency:', currencyData);
            setCurrency({
              code: currencyData.code,
              symbol: currencyData.symbol,
              name: currencyData.name
            });
          }
        }
      } catch (error) {
        console.error('Error loading store settings:', error);
      }
    };

    loadStoreSettings();
  }, []);

  const handleCurrencyChange = (newCurrency: Currency) => {
    console.log('Changing currency to:', newCurrency);
    setCurrency(newCurrency);
  };

  const value = {
    currency,
    setCurrency: handleCurrencyChange
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export type { Currency }; 